import Pristine from 'pristinejs';

import Logger from '../logging/logger';
import triggerEvent, { ContactMethod, GenerateLeadEvent } from '../analytics/ga/events';
import newValidator, { FormValidationError } from '../validation';
import { GAEventName } from '../analytics/ga/events';
import { navigateTo } from '../http/redirect';
import { getUser } from '../user/user';
import { setupEmailSuggestionFromResult } from '../validation/email-suggest';
import { getLoggedUnhandledErrors } from '../logging/errors';
import { ErrorType, ErrorEvent } from '../analytics/ga/events';
import { parseEscapedJSONString } from '../json/parse';
import { EcommerceItem, pushEcommerceEvent } from '../analytics/ga/ecommerce';
import { addRecaptchaFields, getCaptchaAction, loadRecaptcha, renderRecaptcha } from '../captcha/google';
import { getElementIdentifier, getEngagedLabel } from '../analytics/ga/click-events';
import { getFormInfo } from '../forms/types';
import { parseBool } from '../strconv/boolean';

import {
    onPropertyEnquiryClose,
    onPropertyEnquiryCloseWithNo,
    onPropertyEnquiryCloseWithYes,
} from '../events/popup-close-property-enquiry';
import { getWhatsAppURLForDevice, WhatsAppParams } from '../whatsapp/links';
import { initQRCodeFromImg } from '../qrcode/create';
import { formDataToJSON } from '../json/serialise';
import { isVisible } from '../dom/visibility';
import { getCommonElementParameters, triggerTWCEvent, TWCEnquiryEvent, TWCEventName } from '../analytics/twc/events';
import { captureException, captureMessage } from '../logging/logger';
import { acceptPrivacyConsent } from '../privacy-consent';

interface SuccessfulEnquiryResponse {
    response: string;
    eventName: string;
    lang: string;
    currency: string;
    offer: string;
    refId: string;
    offerSimilar: boolean;
    blacklisted: boolean;
    isSpam: boolean;
    processed: boolean;
    whatsAppUrlParams: WhatsAppParams;
    ecommerceItem: EcommerceItem;
    analyticsParams: Record<string, any>;
}

interface FailedEnquiryResponse {
    msg: string;
    reason: string;
    emailSuggestion: string;
	emailSuggestionHTML: string;
    inputName?: string;
    inputValue?: string;
}

interface SubmitOptions {
    forWhatsApp: boolean;
    executeRecaptcha: boolean;
    submiter: HTMLElement | null;
}

const defaultSubmitOptions: SubmitOptions = {
    forWhatsApp: false,
    executeRecaptcha: true,
    submiter: null,
};

function isTrustedEvent( event: Event ): boolean {
    if ( event.isTrusted ) {
        return true;
    }
    return !event.isTrusted && event instanceof CustomEvent && event.type === 'twc:submit';
}

// eslint-disable-next-line @typescript-eslint/naming-convention
function PropertyEnquiryForm( form: HTMLFormElement ) {
    if ( form.dataset.initialised && form.dataset.initialised === 'true' ) {
        return;
    }

    Logger.info( 'Initialising property enquiry form' );

    form.dataset.initialised = 'true';

    const submitBtns = form.querySelectorAll<HTMLButtonElement>( 'button[type=submit]' );

    let validator: Pristine | null = null;

    const handleFormSubmit = function( originalEvent: Event, options = defaultSubmitOptions ): void {
        if ( !isTrustedEvent( originalEvent ) ) {
            return;
        }

        if ( !validator ) {
            validator = newValidator( form );
        }

        handlePropertyEnquiry( form, originalEvent, options );
    };

    form.addEventListener( 'keypress', ( event: KeyboardEvent ) => {
        if ( event.key !== 'Enter' ) {
            return;
        }

        if ( event.target && event.target instanceof HTMLElement && event.target.nodeName === 'TEXTAREA' ) {
            return;
        }

        event.preventDefault();
    } );

    form.addEventListener( 'submit', ( event: SubmitEvent ) => {
        event.preventDefault();

        handleFormSubmit( event, {
            forWhatsApp: ( !!event.submitter && !!event.submitter.dataset.action && event.submitter.dataset.action === 'submit-with-whatsapp' ),
            executeRecaptcha: true,
            submiter: event.submitter,
        } );
    } );

    form.addEventListener( 'twc:submit', ( event: CustomEvent ) => handleFormSubmit( event ) );

    const whatsappSubmitLinks = form.querySelectorAll<HTMLAnchorElement>( 'a[data-action="submit-with-whatsapp"]' );

    whatsappSubmitLinks.forEach( ( whatsappLink: HTMLAnchorElement ) => {
        whatsappLink.addEventListener( 'click', ( event: MouseEvent ) => {
            event.preventDefault();

            handleFormSubmit( event, {
                forWhatsApp: true,
                executeRecaptcha: true,
                submiter: whatsappLink,
            } );
        } );
    } );

    let captchaWidgetID: number | undefined;

    const messageContainer = form.querySelector( '.messages' ) as HTMLElement;

    const errContainer = form.querySelector<HTMLElement>( '.general-error-container' );

    function hideGeneralErr(): void {
        if ( !errContainer ) {
            return;
        }
        errContainer.classList.add( 'd-none' );
    }

    function showGeneralErr(): void {
        if ( !errContainer ) {
            return;
        }
        errContainer.classList.remove( 'd-none' );
    }

    function hideGeneralErrIfResolved(): void {
        if ( !validator ) {
            return;
        }

        if ( !validator.validate( true ) ) {
            return;
        }

        hideGeneralErr();
    }

    [
        'input',
        'change',
    ].forEach( ( eventName: string ) => {
        form.addEventListener( eventName, hideGeneralErrIfResolved );
    } );

    function startLoading( submiter: HTMLElement | null ): void {
        form.dataset.isProcessing = 'true';
        form.classList.add( 'loading' );
        submitBtns.forEach( ( btn: HTMLButtonElement ) => btn.disabled = true );

        if ( submiter ) {
            submiter.classList.add( 'loading' );
        }
    }

    function stopLoading( submiter: HTMLElement | null ): void {
        form.dataset.isProcessing = 'false';
        form.classList.remove( 'loading' );
        submitBtns.forEach( ( btn: HTMLButtonElement ) => btn.disabled = false );

        if ( submiter ) {
            submiter.classList.remove( 'loading' );
        }
    }

    const handlePropertyEnquiry = async function( form: HTMLFormElement, originalEvent: Event, options: SubmitOptions ) {
        if ( parseBool( form.dataset.isProcessing || 'false' ) || parseBool( form.dataset.disabled || 'false' ) ) {
            return;
        }

        Logger.info( 'Handling property enquiry form submission', {
            form,
            options,
            originalEvent,
        } );

        const debugInfo = {
            'form': formDataToJSON( new FormData( form ) ),
            'formIsVisible': isVisible( form ),
            'forWhatsApp': options.forWhatsApp,
            'executeRecaptcha': options.executeRecaptcha,
            'submitter': options.submiter ? getElementIdentifier( options.submiter ) : null,
            'submitterIsVisible': options.submiter ? isVisible( options.submiter ) : false,
            'originalEvent': JSON.stringify( {
                'type': originalEvent.type,
                'isTrusted': originalEvent.isTrusted,
                'target': ( originalEvent.target && originalEvent.target instanceof HTMLElement ) ? getElementIdentifier( originalEvent.target as HTMLElement ) : 'null',
            } ),
            'logs': JSON.stringify( ( window.appLogs || [] ).map( ( logEntry ) => {
                return logEntry[ 0 ];
            } ) ),
        };

        // we need to check if event is not trusted
        if ( !isTrustedEvent( originalEvent ) ) {
            captureMessage( 'Property enquiry form submitted by untrusted event', {
                extra: debugInfo,
            } );
        }

        startLoading( options.submiter );

        hideGeneralErr();

        validator.reset();

        let valid = validator.validate();

        const telephoneInput = form.querySelector<HTMLInputElement>( 'input[name=phone]' );

        if ( options.forWhatsApp &&
            telephoneInput &&
            !telephoneInput.required &&
            telephoneInput.value.length === 0
        ) {
            validator.addError( telephoneInput, telephoneInput.dataset.pristineRequiredMessage );
            valid = false;
        }

        if ( captchaWidgetID ) {
            const haveCaptchaResponse = Array.from( form.querySelectorAll<HTMLInputElement>( '[name="g-recaptcha-response"]' ) ).some( ( element: HTMLInputElement ) => element.value.length > 0 );

            if ( !haveCaptchaResponse ) {
                valid = false;
            }
        }

        const errors: Array<FormValidationError> = validator.getErrors();

        if ( errors.length > 0 ) {
            errors.forEach( ( err: FormValidationError ) => {
                triggerEvent<ErrorEvent>( GAEventName.Error, {
                    'error_type': ErrorType.ErrForm,
                    'error_message': err.errors[ 0 ],
                    'engaged_target': err.input.value,
                    'engaged_element': err.input.name,
                } );
            } );
        }

        if ( !valid ) {
            stopLoading( options.submiter );
            showGeneralErr();
            return;
        }

        form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>( 'input,textarea' ).forEach( ( el: HTMLInputElement | HTMLTextAreaElement ) => {
            el.value = el.value.trim();
        } );

        messageContainer.innerHTML = '';

        const handleSuccessfulEnquiryResponse = function( response: SuccessfulEnquiryResponse, originalEvent: Event, formData: FormData ): void {
            acceptPrivacyConsent( form );

            if ( !isTrustedEvent( originalEvent ) ) {
                captureMessage( 'Property enquiry successful response submitted by untrusted event', {
                    extra: {
                        debugInfo,
                        response,
                    },
                } );
            }

            if ( !response.ecommerceItem.item_id ) {
                throw new Error( 'item_id is not defined' );
            }

            const ecommerceItem = response.ecommerceItem;

            if ( response.ecommerceItem.item_list_id === 'no_list_available' ) {
                const selectors = [
                    'data-ecommerce-event',
                    'data-add-to-cart-event',
                    'data-remove-from-cart-event',
                ];

                for ( let i = 0; i < selectors.length; i++ ) {
                    const matchingElementWithEvent = Array.from( document.querySelectorAll<HTMLElement>( `[${selectors[ i ]}]` ) ).find( ( element: HTMLElement ) => {
                        const attrValue = element.getAttribute( selectors[ i ] );

                        if ( !attrValue || attrValue.length === 0 ) {
                            return false;
                        }

                        const event = parseEscapedJSONString( attrValue );

                        return event.ecommerce.items[ 0 ].item_id === response.ecommerceItem.item_id;
                    } );

                    if ( !matchingElementWithEvent ) {
                        continue;
                    }

                    const matchingEvent = parseEscapedJSONString( matchingElementWithEvent.getAttribute( selectors[ i ] ) as string );

                    if (  matchingEvent.ecommerce.items[ 0 ].item_list_id !== 'no_list_available' ) {
                        ecommerceItem[ 'item_list_id' ] = matchingEvent.ecommerce.items[ 0 ].item_list_id;
                        ecommerceItem[ 'item_list_name' ] = matchingEvent.ecommerce.items[ 0 ].item_list_name;
                        break;
                    }
                }
            }

            const contactMethods: Array<ContactMethod> = [
                ContactMethod.Email,
            ];

            if ( options.forWhatsApp ) {
                contactMethods.push( ContactMethod.WhatsApp );
            }

            const formInfo = getFormInfo( form );

            const generateLeadParameters: GenerateLeadEvent = {
                'lead_id': response.refId,
                'item_id': ecommerceItem.item_id,
                'item_name': ecommerceItem.item_name,
                'item_category': ecommerceItem.item_category,
                'item_category2': ecommerceItem.item_category2,
                'item_category3': ecommerceItem.item_category3,
                'item_category4': ecommerceItem.item_category4,
                'item_category5': ecommerceItem.item_category5,
                'item_variant': ecommerceItem.item_variant,
                'item_brand': ecommerceItem.item_brand,
                'currency': window.currencyName,
                'form_location': formInfo.location,
                'contact_methods': contactMethods.sort().join( ',' ),
                'is_spam': response.isSpam.toString(),
            };

            if ( originalEvent instanceof SubmitEvent && originalEvent.submitter ) {
                generateLeadParameters[ 'engaged_label' ] = getEngagedLabel( originalEvent.submitter );
            }

            triggerEvent<GenerateLeadEvent>( GAEventName.GenerateLead, generateLeadParameters );

            pushEcommerceEvent( {
                event: GAEventName.EcommercePurchase,
                ecommerce: {
                    'transaction_id': response.refId,
                    'value': response.ecommerceItem.price,
                    'currency': response.currency,
                    'is_spam': response.isSpam.toString(),
                    'items': [ ecommerceItem ],
                },
            } );

            window.twc.track( response.eventName, {
                refId: response.refId,
                props: [ Number( response.ecommerceItem.item_id ) ],
            } );

            triggerTWCEvent( TWCEventName.Enquiry, {
                ...getCommonElementParameters( form ),
                propertyID: response.analyticsParams.propertyID,
                clientID: response.analyticsParams.clientID,
                locationID: response.analyticsParams.locationID,
                offer: response.analyticsParams.offer,
                ref: response.analyticsParams.ref,
                price: response.analyticsParams.price,
                beds: response.analyticsParams.beds,
                type: response.analyticsParams.type,
                buildSqm: response.analyticsParams.buildSqm,
                enquiryID: response.analyticsParams.enquiryID,
                enquiryRef: response.analyticsParams.enquiryRef,
                productVariant: response.analyticsParams.productVariant,
            } as TWCEnquiryEvent );

            validator.destroy();

            document.body.insertAdjacentHTML( 'beforeend', response.response );

            const qrCode = document.querySelector<HTMLImageElement>( '#enquiry-confirmation-whatsapp-info .qrcode' );

            if ( qrCode ) {
                try {
                    initQRCodeFromImg( qrCode );
                } catch ( err ) {
                    qrCode.remove();
                    captureException( err );
                }
            }

            window.dispatchEvent( new CustomEvent( 'twc:update-saved-status', {
                bubbles: true,
            } ) );

            const enquiryCloseBtn = document.querySelector( '[data-action="property-enquiry-close"]' );
            if ( enquiryCloseBtn ) {
                enquiryCloseBtn.addEventListener( 'click', onPropertyEnquiryClose );
            }

            const enquiryCloseYesBtn = document.querySelector( '[data-action="property-enquiry-close-yes"]' );
            if ( enquiryCloseYesBtn ) {
                enquiryCloseYesBtn.addEventListener( 'click', onPropertyEnquiryCloseWithYes );
            }

            const enquiryCloseNoBtn = document.querySelector( '[data-action="property-enquiry-close-no"]' );
            if ( enquiryCloseNoBtn ) {
                enquiryCloseNoBtn.addEventListener( 'click', onPropertyEnquiryCloseWithNo );
            }

            const whatsappLinks = document.querySelectorAll<HTMLAnchorElement>( '.enquryconfirmation__advertiser-whatsapp a[href^="whatsapp://send"]' );

            whatsappLinks.forEach( ( link: HTMLAnchorElement ) => {
                link.addEventListener( 'click', ( event: MouseEvent ) => {
                    event.preventDefault();
                    navigateTo( new URL( link.href ).toString() );
                } );
            } );

            if ( response.whatsAppUrlParams && response.whatsAppUrlParams.number && response.whatsAppUrlParams.text ) {
                const whatsappURL = getWhatsAppURLForDevice( response.whatsAppUrlParams );
                whatsappSubmitLinks.forEach( ( link: HTMLAnchorElement ) => {
                    link.href = whatsappURL.toString();
                } );
            }

            if ( response.processed && !response.isSpam && options.forWhatsApp && response.whatsAppUrlParams && response.whatsAppUrlParams.number && response.whatsAppUrlParams.text ) {
                const whatsappURL = getWhatsAppURLForDevice( response.whatsAppUrlParams );

                try {
                    if ( /^https:\/\//.test( whatsappURL.toString() ) ) {
                        if ( !window.whatsappCtx || window.whatsappCtx.closed ) {
                            window.whatsappCtx = window.open( whatsappURL, 'whatsappCtx' );
                        } else {
                            window.whatsappCtx.focus();
                        }
                    } else {
                        navigateTo( whatsappURL.toString() );
                    }
                } catch ( err ) {
                    navigateTo( whatsappURL.toString() );
                    captureException( err );
                }
            }
        };

        const handleFailedEnquiryResponse = function( response: FailedEnquiryResponse, formData: FormData ): void {
            if ( !isTrustedEvent( originalEvent ) ) {
                captureMessage( 'Property enquiry failure response submitted by untrusted event', {
                    extra: {
                        debugInfo,
                        response,
                    },
                } );
            }

            if ( response.emailSuggestion && response.emailSuggestionHTML ) {
                triggerEvent<ErrorEvent>( GAEventName.Error, {
                    'error_type': ErrorType.ErrForm,
                    'error_message': 'did-you-mean',
                    'engaged_target': `${formData.get( 'email' ) || ''}>>${response.emailSuggestion}`,
                } );

                setupEmailSuggestionFromResult( {
                    emailSuggestion: response.emailSuggestion,
                    html: response.emailSuggestionHTML,
                }, form );
                return;
            }

            if ( response.msg ) {
                const event = {
                    'error_type': ErrorType.ErrServer,
                    'error_message': response.msg,
                };

                if ( response.inputName ) {
                    event[ 'engaged_element' ] = response.inputName;
                }

                if ( response.inputValue ) {
                    event[ 'engaged_target' ] = response.inputValue;
                }

                triggerEvent<ErrorEvent>( GAEventName.Error, event );

                messageContainer.insertAdjacentHTML( 'beforeend', `<div class="alert alert-warning">${response.msg}</div>` );
                window.scroll( 0, form.getBoundingClientRect().top + window.scrollY - 200 );
            }
        };

        if ( options.executeRecaptcha ) {
            const action = getCaptchaAction( form );

            const token = await loadRecaptcha( action );

            addRecaptchaFields( form, token, action );
        }

        const formData: FormData = await propertySearchFiltersAsFormData( form );

        if ( options.forWhatsApp ) {
            formData.append( 'offer', 'send-whatsapp' );
        }

        const url = new URL( window.location.origin + form.getAttribute( 'action' ) );

        const response: Response = await window.http( url, {
            method: 'POST',
            body: formData,
        } );

        stopLoading( options.submiter );

        if ( response.status === 200 ) {
            const body = await response.json() as SuccessfulEnquiryResponse;
            handleSuccessfulEnquiryResponse( body, originalEvent, formData );
        }

        if ( response.status === 400 ) {
            const body = await response.json() as FailedEnquiryResponse;

            handleFailedEnquiryResponse( body, formData );

            if ( body.reason === 'no-valid-captcha' ) {
                showGeneralErr();

                if ( form.querySelector( '.captchacontainer__placeholder [name="g-recaptcha-response"]' ) ) {
                    window.grecaptcha.enterprise.reset();
                } else {
                    captchaWidgetID = await renderRecaptcha( '.propertyenquiryform .captchacontainer__placeholder', {
                        callback: ( response: string ): void => {
                            hideGeneralErr();

                            handleFormSubmit( originalEvent, {
                                ...options,
                                executeRecaptcha: false,
                            } );
                        },
                        'expired-callback': (): void => {
                            showGeneralErr();
                        },
                        'error-callback': (): void => {
                            showGeneralErr();
                        },
                    }, captchaWidgetID );
                }

                const captchaElement = form.querySelector( '.captchacontainer__placeholder' ) as HTMLElement;

                if ( captchaElement ) {
                    captchaElement.scrollIntoView();
                }

                const hint: any = {
                    extra: {
                        form: {},
                    },
                };

                for ( const entry of formData ) {
                    const v = hint.extra.form[ entry[ 0 ] ] || [];
                    hint.extra.form[ entry[ 0 ] ] = [
                        ...v,
                        entry[ 1 ],
                    ];
                }

                const errors: Array<Error> = getLoggedUnhandledErrors();

                if ( errors.length > 0 ) {
                    hint.extra = {
                        errors: errors,
                    };
                }

                captureException( new Error( 'property enquiry failed due to captcha' ), hint );
            }
        }
    };

    submitBtns.forEach( ( btn: HTMLButtonElement ) => btn.disabled = false );
}

async function propertySearchFiltersAsFormData( form: HTMLFormElement ): Promise<FormData> {
    const formData = new FormData( form );

    formData.append( 'propertyURL', document.location.toString() );
    formData.append( 'ipcountry', getUser().visitorInfo().country );

    const bedsElement = document.querySelector( '[name=beds]' ) as HTMLInputElement;
    const beds = bedsElement ? bedsElement.value : null;

    const bathsElement = document.querySelector( '[name=baths]' ) as HTMLInputElement;
    const baths = bathsElement ? bathsElement.value : null;

    const tags: string[] = [];

    const checkedTags = document.querySelectorAll<HTMLInputElement>( '[name=tags]:checked' );

    checkedTags.forEach( ( el )  => tags.push( el.value ) );

    if ( tags.length > 0 ) {
        formData.append( 'tags', tags.join( ',' ) );
    }

    if ( beds ) {
        formData.append( 'filterBeds', beds );
    }

    if ( baths ) {
        formData.append( 'filterBaths', baths );
    }

    return formData;
}

export default function initPropertyEnquiryForms( container: HTMLElement | Document = document ) {
    container.querySelectorAll<HTMLFormElement>( '.propertyenquiryform' ).forEach( ( form: HTMLFormElement ) => PropertyEnquiryForm( form ) );
}
